import axios from "axios";
import api from "./Api";

export interface ArquivoLeva {
  file: File;
  generatedFileName?: string;
  originalFileName: string;
  progresso: number;
  enviado: boolean;
  erro?: string;
}

export interface EmpresaRapida {
  id: number;
  cnpj: string;
  razaoSocial?: string;
  nomeFantasia?: string;
}

export interface ImportacaoStatus {
  uploadId: number;
  processando: boolean;
  processou: boolean;
  erro: boolean;
  esteiraId?: number | null;
  /** DEVN3-602: etapa da identificação de layout; null fora dela. */
  statusLayout?: StatusLayout | null;
  mensagens: { mensagem: string; tipo?: string; data: string }[];
}

// ── DEVN3-602: identificação de layout ────────────────────────────────────

export type StatusLayout =
  | "ANALISANDO"
  | "AGUARDANDO_CONFIRMACAO_IA"
  | "IDENTIFICANDO"
  | "AJUSTE_MANUAL"
  | "CANCELADO"
  | "PROCESSANDO";

/** Layout de leitura do razão (colunas 1-based, A=1) — espelho de RazaoLayoutInferidoDTO. */
export interface RazaoLayout {
  modoConta: "BLOCO" | "COLUNA";
  linhaCabecalho?: number | null;
  dataColuna: number;
  historicoColuna?: number | null;
  debitoColuna?: number | null;
  creditoColuna?: number | null;
  valorColuna?: number | null;
  indicadorDCColuna?: number | null;
  valorNegativoEh?: "C" | "D";
  saldoColuna?: number | null;
  loteColuna?: number | null;
  contrapartidaColuna?: number | null;
  contaCodigoColuna?: number | null;
  contaNomeColuna?: number | null;
  contaColuna?: number | null;
  contaCabecalhoRegex?: string | null;
  saldoAnteriorTexto?: string | null;
  saldoAnteriorValorColuna?: number | null;
  totalTexto?: string | null;
  linhasIgnorarComecandoCom?: string[];
  sinalSaldo?: number;
}

export interface ArquivoAnalise {
  nome: string;
  impressaoDigital: string;
  situacao: "TEMPLATE" | "CONHECIDO" | "NOVO" | "MESMO_FORMATO";
  grupoId?: string | null;
  custoEstimadoUsd: number;
}

export interface GrupoFormato {
  id: string;
  descricaoFormato: string;
  arquivoExemplo: string;
  quantidadeArquivos: number;
  tokensInputEstimados: number;
  tokensOutputEstimados: number;
  custoEstimadoUsd: number;
  status: "PENDENTE" | "IDENTIFICADO" | "FALHOU";
  tentativas: number;
  custoRealUsd: number;
  layoutSugerido?: RazaoLayout | null;
  conferenciaMensagem?: string | null;
  divergencias: string[];
  confirmacaoDCObrigatoria: boolean;
  colunas: number;
  amostra: { indice: number; celulas: (string | null)[] }[];
}

export interface AnaliseLayout {
  uploadId: number;
  statusLayout: StatusLayout | "";
  modelo: string;
  chamadasEstimadas: number;
  custoEstimadoUsd: number;
  arquivos: ArquivoAnalise[];
  grupos: GrupoFormato[];
}

export interface UploadEmIdentificacao {
  uploadId: number;
  nomeOriginal: string;
  statusLayout: StatusLayout | "";
  empresaId?: number | null;
  dataCadastro?: string | null;
  /** DEVN3-602: leva que falhou (coluna Falha). */
  erro?: boolean;
  etapa?: EtapaLeva | null;
  erroMensagem?: string | null;
  esteiraId?: number | null;
}

/** Etapa em que a leva está/parou (DEVN3-602). */
export type EtapaLeva = "ANALISAR" | "IDENTIFICAR_LAYOUT" | "SEED" | "CHUNKS";

export const ETAPA_TEXTO: Record<EtapaLeva, string> = {
  ANALISAR: "análise do formato",
  IDENTIFICAR_LAYOUT: "identificação do layout com IA",
  SEED: "leitura do arquivo",
  CHUNKS: "gravação dos lançamentos",
};

export interface ValidacaoLayout {
  ok: boolean;
  salvo: boolean;
  mensagem: string;
  divergencias: string[];
  confirmacaoDCObrigatoria: boolean;
  semProvaMatematica: boolean;
  lancamentos: number;
  contas: number;
  previa: {
    linha: number;
    conta: string;
    nomeConta?: string | null;
    data: string;
    historico?: string | null;
    debito: number;
    credito: number;
  }[];
}

export interface ContaPendente {
  contaEmpresa: string;
  descricao?: string;
  saldoFinal: number;
  totalDebito: number;
  totalCredito: number;
}

export interface EventoOpcao {
  id: number;
  nome?: string;
  contaReferencial: string;
  /** DEVN3-602: Banco, Caixa, Fornecedor, Cliente, Tributo... — filtros da tela de mapeamento. */
  categorias?: string[];
}

export type BoardColuna = "Processamento" | "Falha" | "Mapeamento" | "Concluido";

export interface ImportacaoBoardCard {
  esteiraId: number;
  empresaId: number;
  empresaNome?: string;
  cnpj?: string;
  periodoInicio: string;
  periodoFim: string;
  coluna: BoardColuna;
  importando: boolean;
  contasPendentes: number;
  contasClassificadas: number;
  totalContas: number;
  porcentagem: number;
  dataInsercao: string;
  etapaConcluidaEm?: string | null;
  /** DEVN3-602: preenchidos na coluna Falha. */
  uploadId?: number | null;
  etapa?: EtapaLeva | null;
  erroMensagem?: string | null;
}

const EXT_ACEITAS = [".xlsx", ".xls", ".csv"];

class ImportarRazaoService {
  private ext(name: string) {
    const i = name.lastIndexOf(".");
    return i >= 0 ? name.slice(i).toLowerCase() : "";
  }

  /** Valida no cliente: só arquivos de razão (Excel/CSV) OU um único .zip. Retorna mensagem de erro ou null. */
  validarSelecao(files: File[]): string | null {
    if (files.length === 0) return "Selecione ao menos um arquivo.";
    const exts = files.map((f) => this.ext(f.name));
    if (exts.includes(".zip")) {
      return files.length > 1
        ? "Envie apenas um .zip por vez, ou vários arquivos de razão — não os dois juntos."
        : null;
    }
    const invalidas = Array.from(new Set(exts.filter((e) => !EXT_ACEITAS.includes(e))));
    if (invalidas.length > 0)
      return `Formato(s) não suportado(s): ${invalidas.join(", ")}. Envie .xlsx/.xls, .csv ou um .zip.`;
    return null;
  }

  async cadastrarEmpresa(dados: {
    cnpj: string;
    razaoSocial: string;
    nomeFantasia?: string;
    municipio?: string;
    estado?: string;
  }): Promise<EmpresaRapida> {
    const { data } = await api.post<EmpresaRapida>("importarrazao/empresa", dados);
    return data;
  }

  /**
   * Sobe cada arquivo direto ao GCS por signed URL, atualizando o progresso via callback.
   * Retorna a lista de referências (nome gerado x nome original) para iniciar a importação —
   * não depende do estado do React ter sido atualizado pelo callback.
   */
  async enviarArquivos(
    arquivos: ArquivoLeva[],
    onProgresso: (index: number, patch: Partial<ArquivoLeva>) => void
  ): Promise<{ generatedFileName: string; originalFileName: string }[]> {
    const { data: urls } = await api.post<
      { signedUrl: string; generatedFileName: string; originalFileName: string }[]
    >("importarrazao/generate-urls", {
      arquivos: arquivos.map((a) => ({
        originalFileName: a.originalFileName,
        contentType: a.file.type || "application/octet-stream",
      })),
    });

    const refs: { generatedFileName: string; originalFileName: string }[] = [];
    for (let i = 0; i < arquivos.length; i++) {
      const url = urls[i];
      try {
        await axios.put(url.signedUrl, arquivos[i].file, {
          headers: { "Content-Type": arquivos[i].file.type || "application/octet-stream" },
          onUploadProgress: (e: any) =>
            onProgresso(i, { progresso: Math.round((e.loaded * 100) / (e.total ?? 1)) }),
        });
        onProgresso(i, { generatedFileName: url.generatedFileName, enviado: true, progresso: 100 });
        refs.push({ generatedFileName: url.generatedFileName, originalFileName: url.originalFileName });
      } catch (err: any) {
        onProgresso(i, { erro: "Falha ao enviar o arquivo." });
        throw err;
      }
    }
    return refs;
  }

  async iniciarImportacao(body: {
    arquivos: { generatedFileName: string; originalFileName: string }[];
    empresaId: number;
  }): Promise<{ uploadId: number }> {
    const { data } = await api.post<{ uploadId: number }>("importarrazao", body);
    return data;
  }

  async board(): Promise<ImportacaoBoardCard[]> {
    const { data } = await api.get<ImportacaoBoardCard[]>("importarrazao/board");
    return data;
  }

  async status(uploadId: number): Promise<ImportacaoStatus> {
    const { data } = await api.get<ImportacaoStatus>(`importarrazao/${uploadId}/status`);
    return data;
  }

  // ── DEVN3-602: identificação de layout ──────────────────────────────────

  async emIdentificacao(): Promise<UploadEmIdentificacao[]> {
    const { data } = await api.get<UploadEmIdentificacao[]>("importarrazao/em-identificacao");
    return data;
  }

  async analise(uploadId: number): Promise<AnaliseLayout> {
    const { data } = await api.get<AnaliseLayout>(`importarrazao/${uploadId}/analise`);
    return data;
  }

  async confirmarIa(uploadId: number): Promise<void> {
    await api.post(`importarrazao/${uploadId}/confirmar-ia`);
  }

  async cancelar(uploadId: number): Promise<void> {
    await api.post(`importarrazao/${uploadId}/cancelar`);
  }

  /** DEVN3-602: retoma a leva falha da etapa em que parou. */
  async retentar(uploadId: number): Promise<void> {
    await api.post(`importarrazao/${uploadId}/retentar`);
  }

  /** DEVN3-602: tira a leva falha do board sem processar. */
  async descartar(uploadId: number): Promise<void> {
    await api.post(`importarrazao/${uploadId}/descartar`);
  }

  async validarLayoutManual(
    uploadId: number,
    body: { grupoId: string; layout: RazaoLayout; salvar: boolean; confirmouDebitoCredito: boolean }
  ): Promise<ValidacaoLayout> {
    const { data } = await api.post<ValidacaoLayout>(`importarrazao/${uploadId}/layout-manual`, body);
    return data;
  }

  async contasPendentes(esteiraId: number): Promise<ContaPendente[]> {
    const { data } = await api.get<ContaPendente[]>(`importarrazao/${esteiraId}/contas-pendentes`);
    return data;
  }

  async eventos(): Promise<EventoOpcao[]> {
    const { data } = await api.get<EventoOpcao[]>("importarrazao/eventos");
    return data;
  }

  async classificar(
    esteiraId: number,
    contasEmpresa: string[],
    eventoId: number
  ): Promise<{ classificadas: number; pendentes: number; contaReferencial: string }> {
    const { data } = await api.post(`importarrazao/${esteiraId}/classificar`, {
      contasEmpresa,
      eventoId,
    });
    return data;
  }

  async concluir(
    esteiraId: number
  ): Promise<{ concluida: boolean; pendentes: number; mensagem?: string }> {
    const { data } = await api.post(`importarrazao/${esteiraId}/concluir`);
    return data;
  }
}

export default new ImportarRazaoService();
